import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  createDeviceMapping,
  deleteDeviceMapping,
  getDevices,
  getDeviceMappings,
  getSettingsTemplates,
  previewDeviceMapping,
  testDeviceMapping,
  updateDeviceMapping,
} from '../api/alertApi';
import ImportLegacyDevicesModal from '../components/ImportLegacyDevicesModal';

const STEPS = [
  { id: 'device', title: '1. Device' },
  { id: 'template', title: '2. Template' },
  { id: 'recipients', title: '3. Recipients & Schedule' },
  { id: 'review', title: '4. Review' },
];

const emptyCustomVariableRow = () => ({ key: '', value: '' });

const emptyForm = () => ({
  device_id: '',
  recipient_name: '',
  template_id: '',
  email: '',
  phone_number: '',
  send_time: '',
  is_active: true,
  customVariableRows: [emptyCustomVariableRow()],
});

function normalizeRow(row) {
  let customVariables = row.custom_variables || {};
  if (typeof customVariables === 'string') {
    try {
      customVariables = JSON.parse(customVariables);
    } catch {
      customVariables = {};
    }
  }
  const entries = Object.entries(customVariables);
  return {
    device_id: row.device_id || '',
    recipient_name: row.recipient_name || '',
    template_id: row.template_id ? String(row.template_id) : '',
    email: row.email || '',
    phone_number: row.phone_number || '',
    send_time: row.send_time ? row.send_time.slice(0, 5) : '',
    is_active: row.is_active !== false,
    customVariableRows: entries.length
      ? entries.map(([key, value]) => ({ key, value: String(value) }))
      : [emptyCustomVariableRow()],
  };
}

function buildPayload(form) {
  const custom_variables = {};
  for (const row of form.customVariableRows) {
    const key = row.key.trim();
    if (!key) continue;
    custom_variables[key] = row.value;
  }

  return {
    device_id: form.device_id.trim(),
    template_id: Number(form.template_id),
    recipient_name: form.recipient_name.trim(),
    email: form.email.trim(),
    phone_number: form.phone_number.trim(),
    send_time: form.send_time || null,
    custom_variables,
    is_active: !!form.is_active,
  };
}

function RecipientFields({ form, updateField, showLabel }) {
  return (
    <div style={fieldGrid}>
      <label style={field}>
        <span style={label}>Email address(es), comma-separated</span>
        <input style={input} placeholder="ops@example.com, alerts@example.com" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
      </label>
      <label style={field}>
        <span style={label}>WhatsApp number(s), comma-separated</span>
        <input style={input} placeholder="919999999999" value={form.phone_number} onChange={(e) => updateField('phone_number', e.target.value)} />
      </label>
      <label style={field}>
        <span style={label}>Send Time (IST)</span>
        <input type="time" style={input} value={form.send_time} onChange={(e) => updateField('send_time', e.target.value)} />
      </label>
      <label style={{ ...field, justifyContent: 'flex-end' }}>
        <span style={label}>Mapping Active</span>
        <input type="checkbox" checked={form.is_active} onChange={(e) => updateField('is_active', e.target.checked)} style={{ width: 18, height: 18 }} />
      </label>
      {showLabel && (
        <label style={{ ...field, gridColumn: '1 / -1' }}>
          <span style={label}>Recipient Label (optional)</span>
          <input style={input} placeholder="Site supervisor" value={form.recipient_name} onChange={(e) => updateField('recipient_name', e.target.value)} />
        </label>
      )}
    </div>
  );
}

function CustomVariableFields({ rows, onUpdate, onAdd, onRemove }) {
  return (
    <div>
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map((row, index) => (
          <div key={index} style={customVarRow}>
            <input style={input} placeholder="location" value={row.key} onChange={(e) => onUpdate(index, 'key', e.target.value)} />
            <input style={input} placeholder="Plant A - North Block" value={row.value} onChange={(e) => onUpdate(index, 'value', e.target.value)} />
            <button style={ghostButton} onClick={() => onRemove(index)}>Remove</button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <button style={secondaryButton} onClick={onAdd}>Add Variable</button>
      </div>
      <div style={{ marginTop: 10, color: '#64748b', fontSize: 13 }}>
        Define extra variables specific to this device (e.g. <code>location</code>) to use in the
        email/WhatsApp message via <code>{'{{location}}'}</code>. These are merged with the
        template's fetched data and built-in variables (<code>device_id</code>, <code>date</code>,
        <code>time</code>, etc.).
      </div>
    </div>
  );
}

export default function DeviceMappings() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [activeStep, setActiveStep] = useState('device');
  const [previewResult, setPreviewResult] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const mappingsQuery = useQuery({ queryKey: ['device-mappings'], queryFn: getDeviceMappings });
  const templatesQuery = useQuery({ queryKey: ['settings-templates'], queryFn: getSettingsTemplates });
  const legacyDevicesQuery = useQuery({ queryKey: ['devices'], queryFn: getDevices });

  const mappings = mappingsQuery.data?.data || [];
  const allTemplates = templatesQuery.data?.data || [];
  const templates = allTemplates.filter((t) => t.is_active);
  const selectedTemplate = allTemplates.find((t) => String(t.id) === String(form.template_id));
  const selectedTemplateDisabled = !!selectedTemplate && !selectedTemplate.is_active;

  const legacyDevices = legacyDevicesQuery.data?.data || [];
  const mappedDeviceIds = new Set(mappings.map((m) => m.device_id));
  const unmappedDeviceIds = legacyDevices.map((d) => d.device_id).filter((id) => !mappedDeviceIds.has(id));

  function updateField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function updateCustomVariable(index, key, value) {
    setForm((current) => ({
      ...current,
      customVariableRows: current.customVariableRows.map((row, idx) => idx === index ? { ...row, [key]: value } : row),
    }));
  }

  function addCustomVariable() {
    setForm((current) => ({
      ...current,
      customVariableRows: [...current.customVariableRows, emptyCustomVariableRow()],
    }));
  }

  function removeCustomVariable(index) {
    setForm((current) => ({
      ...current,
      customVariableRows: current.customVariableRows.length === 1
        ? [emptyCustomVariableRow()]
        : current.customVariableRows.filter((_, idx) => idx !== index),
    }));
  }

  function resetForm() {
    setSelectedId(null);
    setForm(emptyForm());
    setPreviewResult(null);
    setActiveStep('device');
  }

  function editMapping(row) {
    setSelectedId(row.id);
    setForm(normalizeRow(row));
    setPreviewResult(null);
    setActiveStep('device');
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload(form);
      if (!payload.device_id) throw new Error('Device ID is required');
      if (!payload.template_id) throw new Error('Please select a template');

      return selectedId
        ? updateDeviceMapping(selectedId, payload)
        : createDeviceMapping(payload);
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['device-mappings'] });
      if (result?.data?.id) {
        setSelectedId(result.data.id);
        setForm(normalizeRow(result.data));
      }
      toast.success('Device mapping saved');
    },
    onError: (err) => toast.error(err.message || err.response?.data?.error || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteDeviceMapping(selectedId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device-mappings'] });
      resetForm();
      toast.success('Device mapping deleted');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Delete failed'),
  });

  const previewMutation = useMutation({
    mutationFn: () => previewDeviceMapping(selectedId),
    onSuccess: (result) => {
      setPreviewResult(result);
      toast.success('Preview loaded');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Preview failed'),
  });

  const testMutation = useMutation({
    mutationFn: () => testDeviceMapping(selectedId),
    onSuccess: (result) => {
      setPreviewResult(result);
      toast.success('Test message sent');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Send test failed'),
  });

  useEffect(() => {
    if (!form.template_id && templates.length && !selectedId) {
      updateField('template_id', String(templates[0].id));
    }
  }, [templates.length, form.template_id, selectedId]);

  const activeStepIndex = STEPS.findIndex((step) => step.id === activeStep);

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <div style={hero}>
        <div>
          <h2 style={{ margin: 0, fontSize: 28, color: '#0f172a' }}>Devices Module</h2>
          <p style={{ margin: '8px 0 0', color: '#475569', maxWidth: 760 }}>
            Type any device-id, attach it to a saved template, and configure who gets notified and
            when (IST). The mapping fetches through the template's data source with this device-id
            substituted, so the response always follows the template's standard shape.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexDirection: 'column', alignItems: 'stretch' }}>
          <button style={smallButton} onClick={resetForm}>+ New Device Mapping</button>
          {unmappedDeviceIds.length > 0 && (
            <button style={importButton} onClick={() => setShowImportModal(true)}>
              Import {unmappedDeviceIds.length} legacy device{unmappedDeviceIds.length === 1 ? '' : 's'}
            </button>
          )}
        </div>
      </div>

      {unmappedDeviceIds.length > 0 && (
        <div style={noticeBox}>
          The Dashboard shows {legacyDevices.length} device{legacyDevices.length === 1 ? '' : 's'} from
          the old device registry, but only {mappings.length} mapping{mappings.length === 1 ? '' : 's'} exist
          here — that's why the two lists differ. {unmappedDeviceIds.length} device{unmappedDeviceIds.length === 1 ? '' : 's'} from
          that registry {unmappedDeviceIds.length === 1 ? 'has' : 'have'} no mapping yet.
          Use "Import legacy devices" above to bring {unmappedDeviceIds.length === 1 ? 'it' : 'them'} into this module.
        </div>
      )}

      <div style={card}>
        <div style={sectionTitle}>Configured Device Mappings</div>
        <div style={tableScroll}>
        <table style={tbl}>
          <colgroup>
            <col style={{ width: 110 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 70 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={th}>Device ID</th>
              <th style={th}>Template</th>
              <th style={th}>Email</th>
              <th style={th}>Phone</th>
              <th style={th}>Send Time (IST)</th>
              <th style={th}>Status</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((row) => (
              <tr key={row.id}>
                <td style={{ ...td, wordBreak: 'break-word' }}><code>{row.device_id}</code></td>
                <td style={{ ...td, wordBreak: 'break-word' }}>{row.template?.name || row.template_id}</td>
                <td style={td}><RecipientList value={row.email} /></td>
                <td style={td}><RecipientList value={row.phone_number} /></td>
                <td style={td}>{row.send_time ? row.send_time.slice(0, 5) : '-'}</td>
                <td style={td}>
                  <span style={{ ...statusPill, background: row.is_active ? '#dcfce7' : '#f3f4f6', color: row.is_active ? '#166534' : '#6b7280' }}>
                    {row.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={td}>
                  <button style={ghostButton} onClick={() => editMapping(row)}>Edit</button>
                </td>
              </tr>
            ))}
            {!mappings.length && (
              <tr><td colSpan={7} style={{ ...td, color: '#9ca3af', textAlign: 'center' }}>No device mappings yet</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <div style={cardOuter}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
            {selectedId ? `Editing Mapping #${selectedId}` : 'New Device Mapping'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={primaryButton} onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {selectedId ? 'Save Changes' : 'Create Mapping'}
            </button>
            <button style={secondaryButton} onClick={() => deleteMutation.mutate()} disabled={!selectedId || deleteMutation.isPending}>
              Delete
            </button>
          </div>
        </div>

        <div style={wizardTabs}>
          {STEPS.map((step, index) => (
            <button
              key={step.id}
              style={{
                ...wizardTab,
                background: activeStep === step.id ? '#0f766e' : '#ffffff',
                color: activeStep === step.id ? '#ffffff' : '#0f172a',
                borderColor: activeStep === step.id ? '#0f766e' : '#dbe4f0',
              }}
              onClick={() => setActiveStep(step.id)}
            >
              <span style={wizardIndex}>{index + 1}</span>
              {step.title}
            </button>
          ))}
        </div>

        {activeStep === 'device' && (
          <div style={card}>
            <div style={sectionTitle}>Device Identity</div>
            <div style={fieldGrid}>
              <label style={field}>
                <span style={label}>Device ID</span>
                <input style={input} placeholder="WMSC0021" value={form.device_id} onChange={(e) => updateField('device_id', e.target.value)} />
              </label>
              <label style={field}>
                <span style={label}>Recipient Label (optional)</span>
                <input style={input} placeholder="Site supervisor" value={form.recipient_name} onChange={(e) => updateField('recipient_name', e.target.value)} />
              </label>
            </div>
            <div style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
              Type the device-id you want to notify on. There's no separate device registry — this
              id is substituted directly into the selected template's HTTP URL or MongoDB query
              wherever <code>{'{device_id}'}</code> appears.
            </div>
          </div>
        )}

        {activeStep === 'device' && (
          <div style={card}>
            <div style={sectionTitle}>Custom Variables</div>
            <CustomVariableFields
              rows={form.customVariableRows}
              onUpdate={updateCustomVariable}
              onAdd={addCustomVariable}
              onRemove={removeCustomVariable}
            />
          </div>
        )}

        {activeStep === 'template' && (
          <div style={card}>
            <div style={sectionTitle}>Notification Template</div>
            <label style={field}>
              <span style={label}>Template</span>
              <select style={input} value={form.template_id} onChange={(e) => updateField('template_id', e.target.value)}>
                <option value="">Select a template</option>
                {selectedTemplateDisabled && (
                  <option value={selectedTemplate.id}>{selectedTemplate.name} (disabled)</option>
                )}
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.source_type})</option>
                ))}
              </select>
            </label>
            {selectedTemplateDisabled && (
              <div style={{ marginTop: 12, color: '#b45309', fontSize: 13 }}>
                This template is disabled in Settings. Pick an active template, or re-enable this one first.
              </div>
            )}
            {selectedTemplate && !selectedTemplateDisabled && (
              <div style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
                {selectedTemplate.description || 'No description provided.'} Response shape follows
                this template's variable mapping and message definitions, regardless of device-id.
              </div>
            )}
            {!templates.length && !selectedTemplateDisabled && (
              <div style={{ marginTop: 12, color: '#b45309', fontSize: 13 }}>
                No active templates yet — create or enable one in Settings first.
              </div>
            )}
          </div>
        )}

        {activeStep === 'recipients' && (
          <div style={card}>
            <div style={sectionTitle}>Recipients & Schedule</div>
            <RecipientFields form={form} updateField={updateField} />
            <div style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
              Email and WhatsApp fields can hold multiple values separated by commas. WhatsApp
              numbers must be in full Indian format, e.g. <code>919999999999</code>. The notification
              is sent automatically every day at the chosen IST time.
            </div>
          </div>
        )}

        {activeStep === 'review' && (
          <>
            <div style={card}>
              <div style={sectionTitle}>Review & Edit</div>
              <div style={fieldGrid}>
                <label style={field}>
                  <span style={label}>Device ID</span>
                  <input style={input} value={form.device_id} onChange={(e) => updateField('device_id', e.target.value)} />
                </label>
                <label style={field}>
                  <span style={label}>Template</span>
                  <select style={input} value={form.template_id} onChange={(e) => updateField('template_id', e.target.value)}>
                    <option value="">Select a template</option>
                    {selectedTemplateDisabled && (
                      <option value={selectedTemplate.id}>{selectedTemplate.name} (disabled)</option>
                    )}
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.source_type})</option>
                    ))}
                  </select>
                </label>
              </div>
              <div style={{ marginTop: 14 }}>
                <RecipientFields form={form} updateField={updateField} showLabel />
              </div>
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Custom Variables</div>
                <CustomVariableFields
                  rows={form.customVariableRows}
                  onUpdate={updateCustomVariable}
                  onAdd={addCustomVariable}
                  onRemove={removeCustomVariable}
                />
              </div>

              {selectedId ? (
                <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button style={secondaryButton} onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending}>
                    Preview Fetch
                  </button>
                  <button style={primaryButton} onClick={() => testMutation.mutate()} disabled={testMutation.isPending}>
                    Send Test Now
                  </button>
                </div>
              ) : (
                <div style={{ marginTop: 12, color: '#b45309', fontSize: 13 }}>
                  Save the mapping first to unlock Preview Fetch and Send Test Now.
                </div>
              )}
            </div>

            {previewResult && (
              <div style={cardGrid}>
                <div style={card}>
                  <div style={sectionTitle}>Raw Fetched Data</div>
                  <pre style={pre}>{JSON.stringify(previewResult.rawResponse, null, 2)}</pre>
                </div>
                <div style={card}>
                  <div style={sectionTitle}>Mapped Variables</div>
                  <pre style={pre}>{JSON.stringify(previewResult.variables, null, 2)}</pre>
                </div>
                <div style={card}>
                  <div style={sectionTitle}>Rendered Output</div>
                  <div style={previewLabel}>Email Subject</div>
                  <div style={previewBox}>{previewResult.rendered?.emailSubject || '-'}</div>
                  <div style={previewLabel}>Email Body</div>
                  <pre style={pre}>{previewResult.rendered?.email || '-'}</pre>
                  <div style={previewLabel}>WhatsApp Message</div>
                  <pre style={pre}>
                    {previewResult.rendered?.whatsapp?.mode === 'api_template'
                      ? JSON.stringify(previewResult.rendered.whatsapp, null, 2)
                      : previewResult.rendered?.whatsapp?.message || '-'}
                  </pre>
                  {previewResult.deliveries && (
                    <>
                      <div style={previewLabel}>Delivery Result</div>
                      <pre style={pre}>{JSON.stringify(previewResult.deliveries, null, 2)}</pre>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div style={wizardFooter}>
          <button
            style={secondaryButton}
            onClick={() => setActiveStep(STEPS[Math.max(0, activeStepIndex - 1)].id)}
            disabled={activeStepIndex === 0}
          >
            Previous
          </button>
          <div style={{ color: '#64748b', fontSize: 13 }}>
            Step {activeStepIndex + 1} of {STEPS.length}
          </div>
          {activeStepIndex < STEPS.length - 1 && (
            <button
              style={primaryButton}
              onClick={() => setActiveStep(STEPS[Math.min(STEPS.length - 1, activeStepIndex + 1)].id)}
            >
              Next
            </button>
          )}
        </div>
      </div>

      {showImportModal && (
        <ImportLegacyDevicesModal
          templates={templates}
          unmappedDeviceIds={unmappedDeviceIds}
          onClose={() => setShowImportModal(false)}
          onImported={() => {
            qc.invalidateQueries({ queryKey: ['device-mappings'] });
            setShowImportModal(false);
          }}
        />
      )}
    </div>
  );
}

const hero = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'start',
  gap: 16,
  padding: 24,
  borderRadius: 24,
  border: '1px solid #dbe4f0',
  background: 'linear-gradient(135deg, #fff8ec 0%, #eef8ff 100%)',
};

const cardOuter = {
  display: 'grid',
  gap: 18,
};

const cardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 18,
};

const card = {
  background: '#ffffff',
  border: '1px solid #dbe4f0',
  borderRadius: 20,
  padding: 20,
  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
};

const wizardTabs = {
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
};

const wizardTab = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  border: '1px solid #dbe4f0',
  borderRadius: 999,
  padding: '10px 16px',
  cursor: 'pointer',
  fontWeight: 700,
};

const wizardIndex = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: 'rgba(255,255,255,0.2)',
  border: '1px solid currentColor',
  fontSize: 12,
};

const wizardFooter = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '12px 4px',
};

const sectionTitle = {
  fontSize: 18,
  fontWeight: 700,
  color: '#0f172a',
  marginBottom: 14,
};

const fieldGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 14,
};

const customVarRow = {
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 1fr) minmax(220px, 2fr) auto',
  gap: 10,
};

const field = {
  display: 'grid',
  gap: 6,
};

const label = {
  color: '#334155',
  fontSize: 13,
  fontWeight: 600,
};

const input = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #cbd5e1',
  borderRadius: 12,
  padding: '10px 12px',
  fontSize: 14,
  color: '#0f172a',
  background: '#fff',
};

const primaryButton = {
  border: 'none',
  borderRadius: 999,
  padding: '10px 18px',
  background: '#0f766e',
  color: '#fff',
  fontWeight: 700,
  cursor: 'pointer',
};

const secondaryButton = {
  border: '1px solid #94a3b8',
  borderRadius: 999,
  padding: '10px 18px',
  background: '#fff',
  color: '#0f172a',
  fontWeight: 700,
  cursor: 'pointer',
};

const smallButton = {
  border: 'none',
  borderRadius: 999,
  padding: '8px 14px',
  background: '#0f766e',
  color: '#fff',
  fontWeight: 700,
  cursor: 'pointer',
};

const importButton = {
  border: '1px solid #94a3b8',
  borderRadius: 999,
  padding: '8px 14px',
  background: '#fff',
  color: '#0f172a',
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: 13,
};

const noticeBox = {
  border: '1px solid #bfdbfe',
  borderRadius: 16,
  background: '#eff6ff',
  padding: '14px 18px',
  color: '#1e3a8a',
  fontSize: 13,
  lineHeight: 1.6,
};

const ghostButton = {
  border: '1px solid #cbd5e1',
  borderRadius: 10,
  padding: '6px 12px',
  background: '#f8fafc',
  color: '#334155',
  cursor: 'pointer',
  fontSize: 13,
};

const previewBox = {
  border: '1px solid #dbe4f0',
  borderRadius: 12,
  background: '#f8fafc',
  padding: 12,
  color: '#0f172a',
  marginBottom: 12,
};

const previewLabel = {
  margin: '8px 0 6px',
  fontSize: 13,
  fontWeight: 700,
  color: '#334155',
};

const pre = {
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: '#f8fafc',
  border: '1px solid #dbe4f0',
  borderRadius: 12,
  padding: 12,
  color: '#0f172a',
  fontSize: 13,
  overflow: 'auto',
};

const statusPill = {
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
};

const tableScroll = { width: '100%', overflowX: 'auto' };
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' };
const th  = { padding: '7px 10px', background: '#f9fafb', textAlign: 'left', fontWeight: 600 };
const td  = { padding: '7px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
const recipientCell = { display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 220 };
const recipientChip = {
  background: '#f3f4f6',
  color: '#374151',
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 12,
  whiteSpace: 'nowrap',
};

function RecipientList({ value }) {
  const items = String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  if (!items.length) return <span style={{ color: '#9ca3af' }}>-</span>;

  return (
    <div style={recipientCell}>
      {items.map((item, idx) => (
        <span key={idx} style={recipientChip} title={item}>{item}</span>
      ))}
    </div>
  );
}
